import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';

// Define the available languages
export type Language = 'en' | 'fr';

// Define the translation interface
interface Translations {
  [key: string]: string;
}

// Define the language context interface
interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  availableLanguages: { code: Language; name: string }[];
}

// Create the context
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Helper function to detect browser language
const detectBrowserLanguage = (): Language => {
  // Check localStorage first for user's saved preference
  const savedLanguage = localStorage.getItem('beanbuzz-language') as Language;
  if (savedLanguage && (savedLanguage === 'en' || savedLanguage === 'fr')) {
    return savedLanguage;
  }

  // Detect browser language
  const browserLanguage = navigator.language || navigator.languages?.[0] || 'en';
  
  // Check if browser language is French
  if (browserLanguage.startsWith('fr')) {
    return 'fr';
  }
  
  // Default to English for all other languages
  return 'en';
};

// Translation data
const translations: Record<Language, Translations> = {
  en: {
    // Header and navigation
    'order.from.table': 'Order from Your Table',
    'order.description': 'Select items from our menu and place your order directly',
    'table.label': 'Table',
    'loading.menu.tables': 'Loading Menu & Tables',
    'loading.menu.tables.description': 'Please wait while we prepare your ordering experience...',
    'loading.menu': 'Loading Menu...',
    'loading.menu.description': 'Please wait while we load the menu items and tables.',
    'loading.processing': 'Processing...',
    
    // Error messages
    'error.invalid.url': 'Invalid URL',
    'error.invalid.url.description': 'The cafe URL is invalid. Please check the QR code or contact staff.',
    'error.table.unavailable': 'Table Unavailable',
    'error.table.unavailable.description': 'This table is currently not accepting orders. Please contact staff for assistance.',
    'error.table.not.found': 'Table Not Found',
    'error.table.not.found.description': 'The table you\'re looking for doesn\'t exist or is no longer available.',
    'error.load.failed': 'Failed to load menu and tables. Please try again.',
    'error.order.failed': 'Failed to place order. Please try again or contact staff for assistance.',
    'error.organization.load.failed': 'Failed to load cafe information',
    
    // Form labels and placeholders
    'form.name.label': 'Name *',
    'form.name.placeholder': 'Enter your name',
    'form.table.label': 'Table *',
    'form.table.select.placeholder': 'Select a table',
    'form.table.no.tables': 'No tables available',
    'form.table.no.tables.description': 'No tables are currently available for ordering.',
    'form.notes.label': 'Special Requests',
    'form.notes.placeholder': 'Any special requests or dietary requirements...',
    
    // Order section
    'order.your.order': 'Your Order',
    'order.items': 'items',
    'order.summary': 'Order Summary',
    'order.total': 'Total',
    'order.place': 'Place Order',
    'order.add.items': 'Add Items to Order',
    'order.place.total': 'Place Order - $',
      'order.ready.finalize': 'Ready to finalize the order',
    'category.collapse': 'Collapse category',
    
    // Notifications
    'notification.enter.name': 'Please enter your name.',
    'notification.add.items': 'Please add items to your cart.',
    'notification.select.table': 'Please select a table.',
    'notification.order.success': 'Order placed successfully! Your order number is #',
    'notification.order.success.description': 'We\'ll notify you when it\'s ready.',
    'notification.cafe.not.accepting': 'Cafe is currently not accepting orders. Please check the status above.',
    'button.cafe.not.accepting': 'Cafe Not Accepting Orders',
    
    // Navigation
    'nav.back.home': 'Back to Home',
    'nav.back.menu': 'Back to Menu',
    
    // Language selector
    'language.english': 'English',
    'language.french': 'Français',
    'language.selector': 'Language',
    
    // Cafe status messages
    'cafe.status.busy.title': 'We\'re Busy!',
    'cafe.status.busy.subtitle': 'Please check back in a few minutes or visit our counter for faster service.',
    'cafe.status.closed.title': 'We\'re Closed',
    'cafe.status.closed.subtitle': 'Thank you for your understanding. We look forward to serving you during our business hours.',
  },
  fr: {
    // Header and navigation
    'order.from.table': 'Commander depuis votre table',
    'order.description': 'Sélectionnez des articles dans notre menu et passez votre commande directement',
    'table.label': 'Table',
    'loading.menu.tables': 'Chargement du menu et des tables',
    'loading.menu.tables.description': 'Veuillez patienter pendant que nous préparons votre expérience de commande...',
    'loading.menu': 'Chargement du menu...',
    'loading.menu.description': 'Veuillez patienter pendant que nous chargeons les articles du menu et les tables.',
    'loading.processing': 'Traitement en cours...',
    
    // Error messages
    'error.invalid.url': 'URL invalide',
    'error.invalid.url.description': 'L\'URL du café est invalide. Veuillez vérifier le code QR ou contacter le personnel.',
    'error.table.unavailable': 'Table indisponible',
    'error.table.unavailable.description': 'Cette table n\'accepte actuellement pas de commandes. Veuillez contacter le personnel pour obtenir de l\'aide.',
    'error.table.not.found': 'Table introuvable',
    'error.table.not.found.description': 'La table que vous recherchez n\'existe pas ou n\'est plus disponible.',
    'error.load.failed': 'Échec du chargement du menu et des tables. Veuillez réessayer.',
    'error.order.failed': 'Échec de la commande. Veuillez réessayer ou contacter le personnel pour obtenir de l\'aide.',
    'error.organization.load.failed': 'Échec du chargement des informations du café',
    
    // Form labels and placeholders
    'form.name.label': 'Nom *',
    'form.name.placeholder': 'Entrez votre nom',
    'form.table.label': 'Table *',
    'form.table.select.placeholder': 'Sélectionnez une table',
    'form.table.no.tables': 'Aucune table disponible',
    'form.table.no.tables.description': 'Aucune table n\'est actuellement disponible pour la commande.',
    'form.notes.label': 'Demandes spéciales',
    'form.notes.placeholder': 'Toute demande spéciale ou exigence alimentaire...',
    
    // Order section
    'order.your.order': 'Votre commande',
    'order.items': 'articles',
    'order.summary': 'Résumé de la commande',
    'order.total': 'Total',
    'order.place': 'Passer la commande',
    'order.add.items': 'Ajouter des articles à la commande',
    'order.place.total': 'Passer la commande - ',
      'order.ready.finalize': 'Prêt à finaliser la commande',
    'category.collapse': 'Réduire la catégorie',
    
    // Notifications
    'notification.enter.name': 'Veuillez entrer votre nom.',
    'notification.add.items': 'Veuillez ajouter des articles à votre panier.',
    'notification.select.table': 'Veuillez sélectionner une table.',
    'notification.order.success': 'Commande passée avec succès ! Votre numéro de commande est #',
    'notification.order.success.description': 'Nous vous notifierons quand elle sera prête.',
    'notification.cafe.not.accepting': 'Le café n\'accepte actuellement pas de commandes. Veuillez vérifier le statut ci-dessus.',
    'button.cafe.not.accepting': 'Le café n\'accepte pas de commandes',
    
    // Navigation
    'nav.back.home': 'Retour à l\'accueil',
    'nav.back.menu': 'Retour au menu',
    
    // Language selector
    'language.english': 'English',
    'language.french': 'Français',
    'language.selector': 'Langue',
    
    // Cafe status messages
    'cafe.status.busy.title': 'Nous sommes occupés !',
    'cafe.status.busy.subtitle': 'Veuillez revenir dans quelques minutes ou visitez notre comptoir pour un service plus rapide.',
    'cafe.status.closed.title': 'Nous sommes fermés',
    'cafe.status.closed.subtitle': 'Merci de votre compréhension. Nous avons hâte de vous servir pendant nos heures d\'ouverture.',
  }
};

// Available languages
const availableLanguages = [
  { code: 'en' as Language, name: 'English' },
  { code: 'fr' as Language, name: 'Français' }
];

// Language provider component
interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguage] = useState<Language>(() => detectBrowserLanguage());

  // Save language preference to localStorage whenever it changes
  const handleSetLanguage = (newLanguage: Language) => {
    setLanguage(newLanguage);
    localStorage.setItem('beanbuzz-language', newLanguage);
  };

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  const value: LanguageContextType = {
    language,
    setLanguage: handleSetLanguage,
    t,
    availableLanguages
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook to use the language context
export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}; 